const { canonicalGuideLinks } = require('./guidePaths');
// Product-authored Work, Bots, feature and native app guides ship with the app. This catalog is shared
// by the client, API, SSR, sitemap and seed importer so releases stay aligned.
const { pages } = require('./workGuides.json');
const { pages: botPages } = require('./botGuides.json');
const { pages: nativeAppPages } = require('./nativeAppGuides.json');
const { pages: featurePages } = require('./featureGuides.json');
const { pages: learnPages } = require('./learnGuides.json');
const { pages: businessPages } = require('./businessGuides.json');
const { publicGuideCopy } = require('./publicTerminology');
const releasedPages = canonicalGuideLinks([...pages, ...botPages, ...nativeAppPages, ...featurePages, ...learnPages, ...businessPages]);
const index = new Map(releasedPages.map(page => [`${page.guideType}:${page.slug}`, page]));
// Old published database records must not restore a removed product guide.
const RETIRED = new Set(['docs:fast-mode', 'learn:work-game-builder']);
function isRetiredGuide(type, slug) {
  return RETIRED.has(`${type}:${slug}`);
}
function getWorkGuide(type, slug) {
  return index.get(`${type}:${slug}`) || null;
}
function mergeWorkPages(existing = [], type) {
  const shipped = releasedPages.filter(page => !type || page.guideType === type);
  const keys = new Set(shipped.map(page => `${page.guideType}:${page.slug}`));
  const prior = new Map(existing.map(page => [`${page.guideType}:${page.slug}`, page]));
  const released = shipped.map(page => {
    const ogImage = prior.get(`${page.guideType}:${page.slug}`)?.meta?.ogImage;
    return ogImage && !page.meta.ogImage ? { ...page, meta: { ...page.meta, ogImage } } : page;
  });
  return [...existing.filter(page => !isRetiredGuide(page.guideType, page.slug) && !keys.has(`${page.guideType}:${page.slug}`)), ...released];
}
function mergeWorkNavigation(sections = [], type) {
  const shipped = pages.filter(page => page.guideType === type);
  const slugs = new Set(shipped.map(page => page.slug));
  const result = sections.map(section => ({ ...section, items: (section.items || []).filter(item => !isRetiredGuide(type, item.slug) && !slugs.has(item.slug)) })).filter(section => section.items.length);
  const groups = type === 'learn'
    ? [['module-7-agentic-workflows', 'Module 7: Your Work'], ['module-8-work-projects', 'Module 8: Work Projects'], ['module-9-work-in-depth', 'Module 9: Work in depth']]
    : [['work-agent', 'Work']];
  for (const [slug, title] of groups) {
    const group = { slug, title, items: shipped.filter(page => page.navSectionSlug === slug).sort((a, b) => a.navOrder - b.navOrder).map(page => ({ slug: page.slug, label: page.title, icon: page.navIcon, description: page.description, href: page.canonicalPath, keywords: page.searchKeywords })) };
    const prior = sections.findIndex(section => section.slug === slug || section.title === title);
    const extraIndex = result.findIndex(section => section.slug === slug || section.title === title);
    if (extraIndex >= 0) group.items.push(...result.splice(extraIndex, 1)[0].items);
    // Work docs follow Getting Started; workshops follow the foundations.
    const position = type === 'docs' ? (prior >= 0 ? Math.min(prior, result.length) : Math.min(1, result.length)) : result.length;
    result.splice(position, 0, group);
  }
  if (type === 'docs') {
    const titles = { 'getting-started': 'Getting Started', 'core-concepts': 'Core Concepts', features: 'Features', developer: 'Developer', reference: 'Reference', 'native-apps': 'Apps & Remote', business: 'Gab AI Business' };
    const updates = new Map([...botPages, ...nativeAppPages, ...featurePages, ...businessPages].map(page => [page.slug, page]));
    const toItem = page => ({ slug: page.slug, label: page.title, icon: page.navIcon, description: page.description, href: page.canonicalPath, keywords: page.searchKeywords });
    for (const section of result) {
      section.items = section.items.map(item => {
        const page = updates.get(item.slug);
        if (!page) return item;
        updates.delete(item.slug);
        return { ...item, ...toItem(page) };
      });
    }
    for (const page of [...updates.values()].sort((a, b) => a.navOrder - b.navOrder)) {
      const title = titles[page.navSectionSlug];
      let section = result.find(item => item.slug === page.navSectionSlug || item.title === title);
      if (!section) {
        section = { slug: page.navSectionSlug, title, items: [] };
        if (page.navSectionSlug === 'getting-started') result.unshift(section);
        else if (page.navSectionSlug === 'native-apps') {
          const workIndex = result.findIndex(item => item.slug === 'work-agent' || item.title === 'Work');
          result.splice(workIndex + 1, 0, section);
        } else result.push(section);
      }
      section.items.push(toItem(page));
    }
    const appSection = result.find(section => section.slug === 'native-apps' || section.title === titles['native-apps']);
    if (appSection) {
      const order = new Map(nativeAppPages.map(page => [page.slug, page.navOrder]));
      appSection.items.sort((a, b) => (order.get(a.slug) ?? Infinity) - (order.get(b.slug) ?? Infinity));
    }
  }
  return canonicalGuideLinks(publicGuideCopy(result));
}
module.exports = { getWorkGuide, mergeWorkPages, mergeWorkNavigation, isRetiredGuide };
